import type { Customer, Quote } from '../api/types'
import type { QuoteBuilderApi } from '../api/client'
import { QuoteBuilderApiError } from '../api/client'
import { assistantTurn, executeAssistantAction, getAssistantTurns, newTurnId } from './api'
import type {
  AssistantAction,
  AssistantArtifact,
  AssistantSavedTurn,
  AssistantExecuteRequest,
  AssistantExecuteResponse,
  AssistantPreviewField,
  AssistantTurnRequest,
  AssistantTurnResponse,
  AssistantWorker,
} from './types'
import { writeVoiceRequest, type LiveTranscript } from './voice-api'
import type { VoiceClip } from './voice-recorder'

// The conversation store (demo `assistant-conversation.ts`): one per scope and user, kept in the
// query cache so it survives remounts and follows a newly created quote.

/** Model text for display: never raw JSON, ids or debug payloads. */
export function assistantText(value: unknown): string {
  let text = (typeof value === 'string' ? value : '')
    .replace(/```[\s\S]*?(?:```|$)/g, '')
    .replace(/(?:^|\n)[^\n]*what i checked[^\n]*\n(?:[^\n]+\n?)*/gi, '')
  let plain = ''
  let depth = 0
  let quoted = false
  let escaped = false
  for (const char of text) {
    if (!depth && (char === '{' || char === '[')) {
      depth = 1
      continue
    }
    if (depth) {
      if (escaped) {
        escaped = false
        continue
      }
      if (char === '\\' && quoted) {
        escaped = true
        continue
      }
      if (char === '"') quoted = !quoted
      if (!quoted && (char === '{' || char === '[')) depth++
      if (!quoted && (char === '}' || char === ']')) depth--
      continue
    }
    plain += char
  }
  text = plain.replace(/[}\]]/g, '').replace(/\b[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}\b/gi, '')
  return text
    .split('\n')
    .filter(
      (line) =>
        !/requested changes|exact totals|exact phase values|what i checked|^\s*(?:[-*]\s*)?checked\b|["'][\w ]+["']\s*:|\b(?:\w+_id|actor id|product id|quote id|line id|expected[_ ]revision|source[_ ]revision)\b/i.test(
          line,
        ),
    )
    .join('\n')
    .replace(/\b\d+\.\d{3,}\b/g, (number) => Number(number).toLocaleString('en-US', { maximumFractionDigits: 2 }))
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

export function assistantFieldValue(label: string, value: string): string {
  const text = assistantText(value)
  const numeric = text.replace(/[$,%]/g, '')
  if (/^-?\d+(?:\.\d+)?$/.test(numeric)) {
    const number = Number(numeric)
    if (/margin|markup|rate|percent/i.test(label)) return `${number.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`
    if (/sell|net|list|price|cost|total|profit|freight|shipping charge|tax amount|deposit|balance/i.test(label)) {
      return number.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    }
    return number.toLocaleString('en-US', { maximumFractionDigits: 2 })
  }
  return text
}

export function assistantActionDisplay(action: Omit<AssistantAction, 'status'>, quote?: Quote | null) {
  const fields = (values: AssistantPreviewField[]) =>
    values.flatMap((field) => {
      const label = assistantText(field.label)
      const after = assistantFieldValue(label, field.after)
      return label && after
        ? [{ label, before: field.before == null ? null : assistantFieldValue(label, field.before) || null, after }]
        : []
    })
  const products = (action.products || []).map((product) => ({
    ...product,
    image_url:
      quote?.lines?.find((line) => String(line.product_id) === product.id)?.primary_asset?.url || product.image_url || null,
    title: assistantText(product.title),
    sku: assistantText(product.sku),
    manufacturer: assistantText(product.manufacturer),
    fields: fields(product.fields || []),
  }))
  const title = action.kind === 'financing' ? action.title.replace(/^Set financing\b/i, 'Set payment terms') : action.title
  return {
    title: assistantText(title) || 'Quote change',
    detail: assistantText(action.detail),
    products,
    fields: fields(action.fields || []),
  }
}

export type ActionState = Omit<AssistantAction, 'status'> & {
  status: 'pending' | 'applying' | 'applied' | 'rejected' | 'failed' | 'stale'
  error?: string
  order?: number
}

/** An equipment import a turn started or finished (USA-1340, USA-1342), from the turn's artifact. */
export type ImportOf = { id: string; fileName: string }

/** The imports a turn's artifacts name. */
export function importsOf(artifacts: AssistantArtifact[] = []): ImportOf[] {
  return artifacts.flatMap((artifact) => {
    if (artifact.kind !== 'document_import') return []
    const result = artifact.result as { id?: unknown; file_name?: unknown }
    return typeof result.id === 'string' ? [{ id: result.id, fileName: String(result.file_name ?? 'Imported file') }] : []
  })
}

/**
 * The import cards each message shows: an import's card shows once, on the first message that
 * brought it, so an import started from chat is not shown again when its agent pass reports back.
 */
export function importCards(messages: ChatMessage[]): Map<number, ImportOf[]> {
  const seen = new Set<string>()
  const cards = new Map<number, ImportOf[]>()
  for (const message of messages) {
    const imports = message.importOf
      ? [message.importOf]
      : message.role === 'assistant' && message.request?.source !== 'import'
        ? importsOf(message.artifacts)
        : []
    const fresh = imports.filter((item) => !seen.has(item.id))
    for (const item of fresh) seen.add(item.id)
    if (fresh.length) cards.set(message.id, fresh)
  }
  return cards
}

export type ChatMessage = {
  id: number
  role: 'user' | 'assistant'
  content: string
  importOf?: ImportOf
  clip?: VoiceClip
  fileIds?: string[]
  status?: 'queued' | 'sending' | 'sent' | 'failed'
  error?: string
  actions?: ActionState[]
  steps?: string[]
  activity?: string[]
  workers?: AssistantWorker[]
  workerOrder?: number
  questions?: string[]
  request?: AssistantTurnRequest
  turnStatus?: AssistantTurnResponse['status']
  responseOrder?: number
  durable?: boolean
  continued?: boolean
  progress?: string
  running?: boolean
  artifacts?: AssistantArtifact[]
}

export type ConversationState = {
  messages: ChatMessage[]
  transcripts: LiveTranscript[]
  draft: string
  autoApprove: boolean
  pending: number
  quote: Quote | null
  progress: string
}

const STOPPED = 'The assistant stopped before finishing. Retry to continue.'

function importOf(saved: AssistantSavedTurn): ImportOf | undefined {
  return saved.request.source === 'import' ? importsOf(saved.result.artifacts ?? [])[0] : undefined
}

/** One saved turn as its request and reply messages. */
function savedMessages(saved: AssistantSavedTurn, next: () => number): ChatMessage[] {
  const { request, result, running } = saved
  const workerOrder = result.workers?.length ? next() : undefined
  return [
    { id: next(), role: 'user', content: request.message, fileIds: request.file_ids, status: 'sent', importOf: importOf(saved) },
    {
      id: next(),
      role: 'assistant',
      content: running ? '' : result.message,
      request,
      turnStatus: result.status,
      running,
      responseOrder: next(),
      durable: true,
      actions: result.actions.map((action) => ({ ...action, status: action.status || 'pending', order: next() })),
      artifacts: result.artifacts,
      workers: result.workers,
      workerOrder,
      continued: result.error === 'assistant_turn_superseded',
      steps: result.steps.map((step) => step.label),
      questions: running ? [] : result.follow_up_questions,
      error: !running && result.error && result.error !== 'assistant_turn_superseded' ? STOPPED : undefined,
    },
  ]
}

// The original request stays with its reply, including after remounts and retries.
export function createAssistantConversation(
  api: QuoteBuilderApi,
  initialQuote: Quote | null,
  onQuote: (quote: Quote) => void,
  onCustomer?: (customer: Customer) => void,
) {
  let state: ConversationState = {
    messages: [],
    transcripts: [],
    draft: '',
    autoApprove: true,
    pending: 0,
    quote: initialQuote,
    progress: '',
  }
  // Live voice: each delegation runs once; transcript fragments reach the agent once.
  const delegations = new Map<string, Promise<ChatMessage | undefined>>()
  const delegatedInput = new Set<string>()
  let transcriptContextThrough = 0
  let sequence = 0
  let queue = Promise.resolve()
  let interacted = false
  let restoration: Promise<void> | undefined
  let restorationQuoteId: string | undefined
  let refreshing = false
  const listeners = new Set<() => void>()
  const publish = (changes: Partial<ConversationState>) => {
    state = { ...state, ...changes }
    listeners.forEach((listener) => listener())
  }
  const updateMessage = (id: number, changes: Partial<ChatMessage>) =>
    publish({ messages: state.messages.map((message) => (message.id === id ? { ...message, ...changes } : message)) })
  const updateAction = (token: string, changes: Partial<ActionState>) =>
    publish({
      messages: state.messages.map((message) => ({
        ...message,
        actions: message.actions?.map((action) => (action.token === token ? { ...action, ...changes } : action)),
      })),
    })
  const actions = () => state.messages.flatMap((message) => message.actions || [])
  const isContinued = (id: number) => {
    const original = state.messages.find((message) => message.id === id)
    if (original?.continued) return true
    const turn = original?.request?.turn_id
    return (
      !!turn &&
      state.messages.some((message) => message.durable && message.request?.previous_turn_id === turn)
    )
  }
  const acceptQuote = (quote: Quote | null | undefined, notify = false) => {
    if (!quote) return
    if (state.quote?.id === quote.id && (state.quote.revision > quote.revision || (!notify && state.quote.revision === quote.revision))) {
      return
    }
    publish({ quote })
    onQuote(quote)
  }
  const acceptApplied = (result: AssistantExecuteResponse, messageId?: number) => {
    const action = actions().find((item) => item.id === result.action_id)
    if (action) updateAction(action.token, { status: 'applied', result: result.message, error: undefined })
    if (result.customer) onCustomer?.(result.customer)
    if (messageId !== undefined) mergeArtifacts(messageId, result.artifacts)
    acceptQuote(result.quote, true)
  }
  const enqueue = (work: () => Promise<void>) => {
    publish({ pending: state.pending + 1 })
    queue = queue.then(work).finally(() => publish({ pending: state.pending - 1, progress: '' }))
    return queue
  }
  const mergeActions = (id: number, incoming: AssistantAction[]) => {
    const merged = [...(state.messages.find((message) => message.id === id)?.actions || [])]
    for (const action of incoming) {
      const index = merged.findIndex((item) => item.id === action.id)
      const previous = merged[index]
      const next: ActionState = {
        ...previous,
        ...action,
        order: previous?.order ?? ++sequence,
        status: previous && ['applied', 'rejected'].includes(previous.status) ? previous.status : action.status || 'pending',
        error: undefined,
      }
      if (index === -1) merged.push(next)
      else merged[index] = next
    }
    updateMessage(id, { actions: merged })
  }
  const mergeArtifacts = (id: number, incoming?: AssistantArtifact[]) => {
    if (!incoming?.length) return
    const current = state.messages.find((message) => message.id === id)?.artifacts || []
    const unique = new Map([...current, ...incoming].map((artifact) => [JSON.stringify(artifact), artifact]))
    updateMessage(id, { artifacts: [...unique.values()] })
  }
  const mergeWorkers = (id: number, incoming: AssistantWorker[]) => {
    if (!incoming.length) return
    const message = state.messages.find((item) => item.id === id)
    const workers = new Map((message?.workers || []).map((worker) => [worker.id, worker]))
    for (const worker of incoming) {
      workers.set(worker.id, { ...workers.get(worker.id), ...worker, label: worker.label || workers.get(worker.id)?.label })
    }
    updateMessage(id, { workers: [...workers.values()], workerOrder: message?.workerOrder ?? ++sequence })
  }
  const runTurn = async (id: number, request: AssistantTurnRequest) => {
    updateMessage(id, { request, content: '', questions: [], error: undefined, turnStatus: undefined, responseOrder: undefined, progress: 'Working' })
    publish({ progress: 'Working' })
    try {
      const result = await assistantTurn(api, request, (event) => {
        if (event.type === 'progress') {
          const progress = event.status === 'running' ? assistantText(event.label) || 'Working' : 'Working'
          publish({ progress })
          if (event.status === 'running' && !/^(Working|Checking your request|Checking operation schema|Planning the next changes)$/.test(progress)) {
            const message = state.messages.find((item) => item.id === id)
            const activity = message?.activity || []
            updateMessage(id, { progress, activity: activity.at(-1) === progress ? activity : [...activity, progress].slice(-40) })
          }
        }
        if (event.type === 'retry') {
          const progress = `Retrying (${event.attempt} of ${event.max_attempts})`
          publish({ progress })
          updateMessage(id, { progress })
        }
        if (event.type === 'action') mergeActions(id, [event.action])
        if (event.type === 'worker') mergeWorkers(id, [event.worker])
        if (event.type === 'applied') acceptApplied(event.result, id)
      })
      mergeActions(id, result.actions)
      mergeWorkers(id, result.workers || [])
      mergeArtifacts(id, result.artifacts)
      updateMessage(id, {
        content: result.message,
        steps: result.steps.map((step) => step.label),
        questions: result.follow_up_questions,
        turnStatus: result.status || 'complete',
        responseOrder: ++sequence,
        durable: true,
        continued: result.error === 'assistant_turn_superseded',
        error: result.error && result.error !== 'assistant_turn_superseded' ? STOPPED : undefined,
      })
      acceptQuote(result.quote)
    } catch (cause) {
      if (cause instanceof QuoteBuilderApiError && cause.code === 'assistant_turn_superseded') {
        updateMessage(id, { continued: true, turnStatus: 'interrupted', responseOrder: ++sequence, error: undefined })
        return
      }
      const invalid = cause instanceof QuoteBuilderApiError && [400, 403, 404, 413, 422].includes(cause.status)
      updateMessage(id, {
        turnStatus: invalid ? undefined : 'interrupted',
        responseOrder: ++sequence,
        error: invalid
          ? cause.status === 413
            ? 'This message is too long. Shorten it and send it again.'
            : cause.status === 404
              ? 'This quote or conversation is no longer available. Open another quote or start a new conversation.'
              : 'The request could not be used. Update your message and send it again.'
          : 'The connection stopped. Retry to continue; applied changes are saved.',
      })
    }
  }
  const resume = (id: number) => {
    const message = state.messages.find((item) => item.id === id)
    // An import's agent turn runs and retries in the background (USA-1340).
    if (state.pending || message?.running || isContinued(id) || !message?.request?.turn_id || message.request.source === 'import') {
      return queue
    }
    interacted = true
    return enqueue(() => runTurn(id, { ...message.request!, auto_approve: state.autoApprove }))
  }
  const apply = (token: string, approval: AssistantExecuteRequest['approval'], confirmation: string | null = null) => {
    const message = state.messages.find((item) => item.actions?.some((action) => action.token === token))
    const action = message?.actions?.find((item) => item.token === token)
    if (state.pending || message?.running || message?.request?.reject_action_id || !action || !['pending', 'failed'].includes(action.status)) {
      return queue
    }
    if (action.approval === 'confirmation' && (approval !== 'confirmation' || confirmation !== action.title)) return queue
    if (action.approval === 'review' && approval !== 'review') return queue
    interacted = true
    updateAction(token, { status: 'applying', error: undefined })
    return enqueue(async () => {
      try {
        acceptApplied(await executeAssistantAction(api, { token, approval, confirmation }), message?.id)
      } catch (cause) {
        const stale = cause instanceof QuoteBuilderApiError && [410, 412].includes(cause.status)
        updateAction(
          token,
          stale
            ? { status: 'stale', error: 'The quote changed. Ask SmartQuote to review this change again.' }
            : { status: 'failed', error: 'Could not apply this change. Retry, or ask for an updated proposal.' },
        )
        return
      }
      if (message?.request && !state.messages.find((item) => item.id === message.id)?.running) {
        await runTurn(message.id, { ...message.request, auto_approve: state.autoApprove })
      }
    })
  }
  type LiveDelegation = {
    history: AssistantTurnRequest['history']
    result: (message: ChatMessage | undefined) => void
    started?: (message: ChatMessage) => void
  }
  const submit = (
    text: string,
    source: AssistantTurnRequest['source'] = 'chat',
    fileIds: string[] = [],
    { clip, live }: { clip?: VoiceClip; live?: LiveDelegation } = {},
  ) => {
    const attached = [...new Set(fileIds)]
    const content = text.trim() || (attached.length ? 'Use the attached files as context for this quote.' : '')
    if (!content) return queue
    interacted = true
    const id = ++sequence
    publish({
      messages: [...state.messages, { id, role: 'user', content, clip, ...(attached.length ? { fileIds: attached } : {}), status: 'queued' }],
    })
    const turnId = newTurnId()
    const autoApprove = state.autoApprove
    return enqueue(async () => {
      const preceding = state.messages.slice(0, state.messages.findIndex((message) => message.id === id))
      const previousIndex = preceding.map((message) => Boolean(message.durable && message.request?.turn_id)).lastIndexOf(true)
      const previousTurn = preceding[previousIndex]?.request?.turn_id
      const history = preceding
        .slice(previousIndex + 1)
        .filter((message) => message.status !== 'queued' && message.status !== 'failed')
        .map((message) => ({
          role: message.role,
          content: [
            message.content,
            ...(message.actions || []).map((action) => `${action.title}: ${action.status}${action.result ? `. ${action.result}` : ''}`),
          ].join('\n'),
        }))
      // Spoken context the agent has not seen yet, timestamped so overlap and corrections survive.
      const transcriptEnd = state.transcripts.length
      history.push(
        ...state.transcripts
          .slice(transcriptContextThrough)
          .map((item) => ({ role: item.role, content: `[Live ${item.start_ms}-${item.end_ms}ms] ${item.delta}` })),
      )
      const request: AssistantTurnRequest = {
        message: content,
        quote_id: state.quote?.id || null,
        source,
        history: [...history, ...(live?.history || [])],
        turn_id: turnId,
        auto_approve: autoApprove,
        ...(previousTurn ? { previous_turn_id: previousTurn } : {}),
        ...(attached.length ? { file_ids: attached } : {}),
      }
      updateMessage(id, { status: 'sent' })
      const replyId = ++sequence
      const messages = [...state.messages]
      messages.splice(messages.findIndex((message) => message.id === id) + 1, 0, { id: replyId, role: 'assistant', content: '', request })
      publish({ messages })
      live?.started?.(messages.find((message) => message.id === replyId)!)
      await runTurn(replyId, request)
      if (state.messages.find((message) => message.id === replyId)?.durable) transcriptContextThrough = transcriptEnd
      live?.result(state.messages.find((message) => message.id === replyId))
    })
  }
  const restore = () => {
    const quoteId = state.quote?.id || initialQuote?.id || undefined
    if (restorationQuoteId !== quoteId) {
      restoration = undefined
      restorationQuoteId = quoteId
    }
    restoration ??= (async () => {
      try {
        const saved = await getAssistantTurns(api, quoteId)
        // An unsent draft on an existing quote must not discard its saved context.
        if (state.messages.length || state.transcripts.length || (interacted && !quoteId)) return
        const messages = saved.flatMap((turn) => savedMessages(turn, () => ++sequence))
        publish({ messages })
        let latestQuote = [...saved].reverse().find((turn) => turn.result.quote)?.result.quote
        for (const turn of saved) {
          if (turn.result.quote?.id === latestQuote?.id && turn.result.quote && turn.result.quote.revision > latestQuote!.revision) {
            latestQuote = turn.result.quote
          }
        }
        if (latestQuote && (!state.quote || state.quote.id === latestQuote.id)) acceptQuote(latestQuote)
      } catch {
        /* A history outage must not prevent a new conversation. */
      }
    })()
    return restoration
  }
  /** Adds equipment imports' agent turns saved since this conversation loaded (USA-1340). */
  const addImports = async () => {
    const quoteId = state.quote?.id
    if (!quoteId || !restoration || state.pending) return
    try {
      await restoration
      const known = new Set(state.messages.flatMap((message) => (message.request?.turn_id ? [message.request.turn_id] : [])))
      const added = (await getAssistantTurns(api, quoteId)).filter(
        (turn) => turn.request.source === 'import' && turn.request.turn_id && !known.has(turn.request.turn_id),
      )
      if (added.length && state.quote?.id === quoteId) {
        publish({ messages: [...state.messages, ...added.flatMap((turn) => savedMessages(turn, () => ++sequence))] })
      }
    } catch {
      /* The next opening tries again. */
    }
  }
  /** Picks up a turn still running elsewhere (another tab, or a reload mid-turn). */
  const refreshRunning = async () => {
    if (refreshing || state.pending || !state.messages.some((message) => message.running)) return
    refreshing = true
    try {
      const saved = await getAssistantTurns(api, state.quote?.id)
      for (const { request, result, running } of saved) {
        const message = state.messages.find((item) => item.running && item.request?.turn_id === request.turn_id)
        if (!message) continue
        mergeActions(message.id, result.actions)
        mergeWorkers(message.id, result.workers || [])
        mergeArtifacts(message.id, result.artifacts)
        updateMessage(message.id, {
          content: running ? '' : result.message,
          running,
          turnStatus: result.status,
          steps: result.steps.map((step) => step.label),
          questions: running ? [] : result.follow_up_questions,
          continued: result.error === 'assistant_turn_superseded',
          error: !running && result.error && result.error !== 'assistant_turn_superseded' ? STOPPED : undefined,
        })
        acceptQuote(result.quote)
      }
    } catch {
      /* Keep observing after a temporary history outage. */
    } finally {
      refreshing = false
    }
  }
  return {
    getSnapshot: () => state,
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    setDraft: (draft: string) => {
      interacted = true
      publish({ draft })
    },
    setAutoApprove: (autoApprove: boolean) => {
      interacted = true
      publish({ autoApprove })
    },
    addTranscript: (fragment: LiveTranscript) => {
      if (state.transcripts.some((item) => item.sessionId === fragment.sessionId && item.event_id === fragment.event_id)) return
      interacted = true
      publish({ transcripts: [...state.transcripts, { ...fragment, order: ++sequence }] })
    },
    /**
     * Runs what the caller said up to the delegation point as one voice turn, exactly once. The
     * speech is first written out as the request they would have typed (USA-1140); speech with
     * nothing to do starts no turn.
     */
    delegate: (sessionId: string, delegationId: string, offset: number, fileIds: string[], started?: (message: ChatMessage) => void) => {
      const key = `${sessionId}:${delegationId}`
      const existing = delegations.get(key)
      if (existing) return existing
      const fragments = state.transcripts
        .filter((item) => item.sessionId === sessionId && item.start_ms <= offset)
        .sort((a, b) => a.start_ms - b.start_ms)
      const fresh = fragments.filter((item) => item.role === 'user' && !delegatedInput.has(`${sessionId}:${item.event_id}`))
      const content = fresh
        .map((item) => item.delta)
        .join('')
        .trim()
      if (!content) return Promise.resolve(undefined)
      fresh.forEach((item) => delegatedInput.add(`${sessionId}:${item.event_id}`))
      // Timestamped context preserves overlap and corrections; only delegation starts work.
      const history: AssistantTurnRequest['history'] = []
      const current = actions()
        .filter((action) => action.status !== 'rejected')
        .map((action) => `${action.title}: ${action.status}${action.result ? `. ${action.result}` : ''}`)
        .join('\n')
      if (current) history.push({ role: 'assistant', content: `Current action state (speech interruption does not cancel work):\n${current}` })
      const recent = state.messages
        .slice(-12)
        .filter((message) => message.content)
        .map((message) => ({ role: message.role, content: message.content }))
      const result = (async () => {
        const written = await writeVoiceRequest(api, { spoken: content, history: recent })
          .then((response) => response.message.trim())
          .catch(() => content)
        if (!written) return undefined
        return await new Promise<ChatMessage | undefined>((resolve) => {
          void submit(written, 'voice', fileIds, { live: { history, result: resolve, started } }).catch(() => resolve(undefined))
        })
      })()
      delegations.set(key, result)
      return result
    },
    syncQuote: (quote: Quote) => {
      if (!state.quote || (quote.id === state.quote.id && quote.revision > state.quote.revision)) publish({ quote })
    },
    reject: (token: string) => {
      const message = state.messages.find((item) => item.actions?.some((action) => action.token === token))
      const action = message?.actions?.find((item) => item.token === token)
      if (state.pending || message?.running || !message?.request || !action || !['pending', 'failed'].includes(action.status)) {
        return queue
      }
      interacted = true
      const request = { ...message.request, reject_action_id: action.id, auto_approve: state.autoApprove }
      return enqueue(() => runTurn(message.id, request))
    },
    apply,
    submit,
    resume,
    restore,
    addImports,
    refreshRunning,
    isContinued,
  }
}

export type AssistantConversation = ReturnType<typeof createAssistantConversation>
